import { Button } from "@/ui";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useLocation } from "wouter";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const HERO_BLUE = "#0d3b6e";

export default function CTASection() {
  const [, setLocation] = useLocation();

  const handleBookDemo = () => {
    window.scrollTo(0, 0);
    setLocation("/book-demo");
  };

  const handleContact = () => {
    setLocation("/contact");
  };

  return (
    <section className="relative py-20 lg:py-32 overflow-hidden">
      {/* Gradient background */}
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`,
        }}
      />

      {/* Animated floating shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
        <div
          className="absolute -top-10 -right-10 w-64 h-64 rounded-full opacity-20 animate-float"
          style={{ background: "radial-gradient(circle, white 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-15 animate-float-delayed"
          style={{ background: "radial-gradient(circle, white 0%, transparent 70%)" }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-40 h-40 rounded-full opacity-10 animate-float-slow"
          style={{ background: "radial-gradient(circle, white 0%, transparent 70%)" }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 lg:px-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal text-white mb-6 animate-fade-in-up">
          Ready to Transform Your Urgent Care?
        </h2>
        <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-100">
          Join hundreds of clinics already using our AI-native platform to deliver better patient care while reducing
          administrative burden.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-200">
          <Button
            size="lg"
            className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
            style={{
              backgroundColor: "white",
              color: PRIMARY_BLUE,
              boxShadow: "0 4px 0 rgba(255,255,255,0.7), 0 6px 20px rgba(255,255,255,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
            onClick={handleBookDemo}
          >
            Request Live Demo
            <ArrowRight className="w-5 h-5" style={{ color: "#0AB89E" }} />
          </Button>
          <Button
            size="lg"
            className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
            style={{
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "white",
              boxShadow: "0 4px 0 rgba(255,255,255,0.3), 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
            onClick={handleContact}
          >
            <MessageCircle className="w-5 h-5" style={{ color: "#0AB89E" }} />
            Contact Sales
          </Button>
        </div>

        {/* Trust badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 text-white/70 text-sm animate-fade-in-up animation-delay-300">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-400/30 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-green-400" />
            </div>
            <span>HIPAA Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-400/30 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-green-400" />
            </div>
            <span>SOC 2 Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-400/30 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-green-400" />
            </div>
            <span>99.9% Uptime SLA</span>
          </div>
        </div>
      </div>
    </section>
  );
}

